import { searchEntries } from './search';
import { sorterFor } from './trending';
import type { AppState, Entry } from './types';

export const DEFAULT_STATE: AppState = {
  q: '',
  categories: [],
  tags: [],
  type: 'all',
  sort: 'new',
  country: null,
};

/**
 * Pure pipeline: type filter → category filter → country filter → tag filters
 * → search (relevance-ranked with the active sort as tiebreak) → sort.
 * Categories are OR'd (an entry matching any selected category passes); tags are
 * AND'd (an entry must carry every selected tag).
 */
export function applyState(state: AppState, entries: Entry[]): Entry[] {
  let out = entries;
  if (state.type !== 'all') out = out.filter((e) => e.type === state.type);
  if (state.categories.length) {
    const cats = new Set(state.categories);
    out = out.filter((e) => cats.has(e.category));
  }
  if (state.country) out = out.filter((e) => e.country === state.country);
  for (const tag of state.tags) out = out.filter((e) => e.tags.includes(tag));
  const sorter = sorterFor(state.sort);
  if (state.q.trim()) return searchEntries(state.q, out, sorter);
  return [...out].sort(sorter);
}

/** Category → count within the current type filter (ignores active category/tags/search). */
export function categoryCounts(state: AppState, entries: Entry[]): Map<string, number> {
  const pool = state.type === 'all' ? entries : entries.filter((e) => e.type === state.type);
  const counts = new Map<string, number>();
  for (const e of pool) counts.set(e.category, (counts.get(e.category) ?? 0) + 1);
  return counts;
}

/** Most common tags in the current pool (excluding tags that mirror category
 *  names). Restricted to the active categories when any are selected; otherwise
 *  drawn from every entry in the active type — so the tag filter is available
 *  and useful with or without a category chosen. */
export function topTags(state: AppState, entries: Entry[], limit = 10): string[] {
  const cats = state.categories.length ? new Set(state.categories) : null;
  const pool = entries.filter(
    (e) => (!cats || cats.has(e.category)) && (state.type === 'all' || e.type === state.type),
  );
  const counts = new Map<string, number>();
  for (const e of pool) {
    for (const t of e.tags) {
      if (t === e.category) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([t]) => t);
}
